<template>
  <div>
    <v-row dense class="no-print">
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="formData.tanggal_awal"
          type="date"
          label="Dari Tanggal"
          outlined
          clearable
          dense
          autofocus
          @keydown.enter="fetchLaporan"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="formData.tanggal_akhir"
          type="date"
          label="Sampai Tanggal"
          outlined
          clearable
          dense
          @keydown.enter="fetchLaporan"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-btn
          color="primary"
          @click="fetchLaporan"
          :disabled="!formData.tanggal_awal || !formData.tanggal_akhir"
        >
          Cari
        </v-btn>
      </v-col>
    </v-row>

    <v-row
      class="fill-height"
      align-content="center"
      justify="center"
      v-if="state.loading"
    >
      <v-col class="text-subtitle-1 text-center" cols="12">
        Loading ...
      </v-col>
      <v-col cols="6">
        <v-progress-linear
          color="deep-purple accent-4"
          indeterminate
          rounded
          height="6"
        ></v-progress-linear>
      </v-col>
    </v-row>

    <p class="no-print" v-if="state.noData">No Data</p>

    <main
      class="report"
      v-if="!state.noData && !state.loading"
      id="section-print"
    >
      <div class="header">
        <h2 class="report-title">{{ companyName }}</h2>
        <h3 class="report-subtitle">LAPORAN TAGIHAN</h3>
        <h3 class="period-title">
          Periode: {{ formatDate(formData.tanggal_awal) }} s/d
          {{ formatDate(formData.tanggal_akhir) }}
        </h3>
      </div>

      <table width="100%" class="table-border">
        <thead>
          <tr>
            <th>Tanggal Nota</th>
            <th>Kode Kapal</th>
            <th>Kode Container</th>
            <th>Ditagih Kepada</th>
            <th>No Nota</th>
            <th class="text-right">Invoice</th>
            <th>Tgl Lunas</th>
            <th>Kode Pelunasan</th>
            <th class="text-right">Total Pemotongan</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in data.items" :key="item.id">
            <td>{{ formatDate(item.tanggal_nota) }}</td>
            <td>{{ item.kv || "-" }}</td>
            <td>{{ item.kc || "-" }}</td>
            <td>{{ customerName(item.ditagih_kepada) }}</td>
            <td>{{ item.nomor_nota || "-" }}</td>
            <td class="text-right">{{ formatCurrency(item.nominal) }}</td>
            <td>{{ formatDate(item.tgl_lunas) }}</td>
            <td>{{ item.no_pelunasan || "-" }}</td>
            <td class="text-right">
              {{ formatCurrency(item.total_pemotongan) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="7" class="text-right">
              <b>Total Keseluruhan:</b>
            </td>
            <td class="text-right">
              <b>{{ formatCurrency(data.grand_total_nominal) }}</b>
            </td>
            <td class="text-right">
              <b>{{ formatCurrency(data.grand_total_pemotongan) }}</b>
            </td>
          </tr>
        </tfoot>
      </table>

      <div class="summary mt-4">
        <p class="mb-0"><b>Jumlah Nota:</b> {{ data.grand_count }}</p>
      </div>
    </main>

    <v-btn
      class="mx-2 no-print"
      fab
      dark
      right
      bottom
      fixed
      color="success"
      v-if="!state.noData && !state.loading"
      @click="printDocument"
    >
      Print
    </v-btn>
  </div>
</template>

<script>
import axios from "axios";
import variableMixin from "@/mixins/variableMixin";

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

export default {
  mixins: [variableMixin],
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      state: {
        loading: false,
        noData: true
      },
      formData: {
        tanggal_awal: this.formatInputDate(firstDay),
        tanggal_akhir: this.formatInputDate(today)
      },
      customersByValue: {},
      data: {
        items: [],
        grand_total_nominal: 0,
        grand_total_pemotongan: 0,
        grand_count: 0
      }
    };
  },
  methods: {
    printDocument() {
      window.print();
    },
    formatInputDate(dateValue) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    normalizeLegacyMonths(payload) {
      const items = [];
      (payload.months || []).forEach((month, monthIndex) => {
        (month.items || []).forEach((row, rowIndex) => {
          items.push({
            id:
              row.id || `${monthIndex}-${rowIndex}-${row.nomor_nota || "nota"}`,
            tanggal_nota: row.tanggal_nota || row.tanggal,
            nama_kapal: row.nama_kapal,
            voy: row.voy,
            no_container: row.no_container,
            ditagih_kepada: row.ditagih_kepada,
            nomor_nota: row.nomor_nota,
            tgl_lunas: row.tgl_lunas,
            total_pemotongan: toNumber(row.total_pemotongan),
            nominal: toNumber(row.nominal)
          });
        });
      });
      return items;
    },
    normalizePayload(payload) {
      let items = [];

      if (Array.isArray(payload)) {
        items = payload;
      } else if (Array.isArray(payload?.items)) {
        items = payload.items;
      } else if (Array.isArray(payload?.months)) {
        items = this.normalizeLegacyMonths(payload);
      }

      const normalizedItems = (items || []).map((row, index) => ({
        id: row.id || `${index}-${row.nomor_nota || "nota"}`,
        tanggal_nota: row.tanggal_nota || row.tanggal || row.tgl_nota || null,
        kv: row.kv || row.kode_kapal || null,
        nama_kapal: row.nama_kapal || row.nama_vessel || null,
        voy: row.voy || null,
        kc: row.kc || row.kode_container || null,
        no_container: row.no_container || null,
        ditagih_kepada: row.ditagih_kepada || null,
        no_pelunasan: row.no_pelunasan || row.kode_pelunasan || null,
        nomor_nota: row.nomor_nota || null,
        tgl_lunas: row.tgl_lunas || null,
        total_pemotongan: toNumber(row.total_pemotongan),
        nominal: toNumber(row.nominal)
      }));

      const grandTotalNominal = normalizedItems.reduce(
        (acc, row) => acc + row.nominal,
        0
      );
      const grandTotalPemotongan = normalizedItems.reduce(
        (acc, row) => acc + row.total_pemotongan,
        0
      );

      return {
        items: normalizedItems,
        grand_total_nominal:
          toNumber(payload?.grand_total_nominal) || grandTotalNominal,
        grand_total_pemotongan:
          toNumber(payload?.grand_total_pemotongan) || grandTotalPemotongan,
        grand_count: toNumber(payload?.grand_count) || normalizedItems.length
      };
    },
    inSelectedRange(dateValue) {
      if (!dateValue) {
        return false;
      }

      const value = new Date(dateValue);
      const start = new Date(this.formData.tanggal_awal);
      const end = new Date(this.formData.tanggal_akhir);

      if (
        Number.isNaN(value.getTime()) ||
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime())
      ) {
        return false;
      }

      value.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      return value >= start && value <= end;
    },
    normalizeAndFilter(payload) {
      const normalized = this.normalizePayload(payload || {});
      const items = normalized.items.filter(item =>
        this.inSelectedRange(item.tanggal_nota)
      );

      return {
        items,
        grand_total_nominal: items.reduce((acc, row) => acc + row.nominal, 0),
        grand_total_pemotongan: items.reduce(
          (acc, row) => acc + row.total_pemotongan,
          0
        ),
        grand_count: items.length
      };
    },
    async fetchCustomers() {
      try {
        const res = await axios.get(`invoice/customers`);
        const map = {};
        (res.data || []).forEach(item => {
          const value =
            item.status == "penerima"
              ? "PENERIMA-" + item.id
              : "PENGIRIM-" + item.id;
          map[value] = item.nama || value;
        });
        this.customersByValue = map;
      } catch (e) {}
    },
    customerName(value) {
      if (!value) return "-";
      return this.customersByValue[value] || value;
    },
    async requestLaporanTagihan() {
      return axios.get(
        `invoice/laporan-tagihan?tanggal_awal=${this.formData.tanggal_awal}&tanggal_akhir=${this.formData.tanggal_akhir}`
      );
    },
    async requestLegacyOmset() {
      const tahun = new Date(this.formData.tanggal_awal).getFullYear();
      return axios.get(`invoice/laporan-omset-tagihan?tahun=${tahun}`);
    },
    async fetchLaporan() {
      if (!this.formData.tanggal_awal || !this.formData.tanggal_akhir) {
        return;
      }

      this.state.loading = true;

      await this.requestLaporanTagihan()
        .then(res => {
          const normalized = this.normalizeAndFilter(res.data || {});

          if (!normalized.items.length) {
            this.state.noData = true;
            this.data = {
              items: [],
              grand_total_nominal: 0,
              grand_total_pemotongan: 0,
              grand_count: 0
            };
          } else {
            this.state.noData = false;
            this.data = normalized;
          }

          this.state.loading = false;
        })
        .catch(async err => {
          const shouldFallback =
            !err.response || [404, 405, 500].includes(err.response.status);

          if (!shouldFallback) {
            this.state.loading = false;
            alert(err.response?.data?.message || "Gagal memuat laporan");
            return;
          }

          try {
            const legacyResponse = await this.requestLegacyOmset();
            const normalizedLegacy = this.normalizeAndFilter(
              legacyResponse.data || {}
            );

            if (!normalizedLegacy.items.length) {
              this.state.noData = true;
              this.data = {
                items: [],
                grand_total_nominal: 0,
                grand_total_pemotongan: 0,
                grand_count: 0
              };
            } else {
              this.state.noData = false;
              this.data = normalizedLegacy;
            }

            this.state.loading = false;
          } catch (legacyErr) {
            this.state.loading = false;
            alert(legacyErr.response?.data?.message || "Gagal memuat laporan");
          }
        });
    },
    formatDate(date) {
      if (!date) {
        return "-";
      }

      const d = new Date(date);
      if (Number.isNaN(d.getTime())) {
        return "-";
      }

      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("id-ID").format(toNumber(value));
    }
  },
  created() {
    this.fetchCustomers();
    this.fetchLaporan();
  }
};
</script>

<style scoped>
.report {
  background-color: white;
  width: 100%;
  font-family: times;
  padding: 20px;
  color: black;
}

.header {
  margin-bottom: 12px;
  text-align: left;
}

.report-title {
  margin-bottom: 2px;
}

.report-subtitle {
  margin-bottom: 2px;
}

.period-title {
  font-style: italic;
}

.table-border,
.table-border th,
.table-border td {
  border: 1px solid #a8a8a8;
  border-collapse: collapse;
}

.table-border th,
.table-border td {
  padding: 4px 6px;
}

.table-border th {
  text-align: left;
}

.summary {
  text-align: right;
}

@media print {
  .no-print {
    display: none !important;
  }

  .report {
    padding: 0;
    margin: 0;
  }
}
</style>
